import {IGlobalStorefrontSettings, IStorefrontSection, IStorefrontSettings} from "../../models/Storefront";
import {styleValue} from "./Editor/styleConfig";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    CommandBar,
    ICommandBarItemProps,
    Spinner,
    SpinnerSize,
} from "@fluentui/react";
import {
    useDeleteStorefrontSectionMutation,
    useGetStorefrontSectionsQuery,
    useSaveStorefrontSectionMutation,
    useSaveStorefrontSectionsMutation
} from "../../store/endpoints/StorefrontSectionEndpoints";
import {cloneDeep, debounce} from "lodash";
import StorefrontSection from "./StorefrontSection";
import StorefrontSectionTemplateSelectorModal from "./StorefrontSectionTemplateSelectorModal";
import {useGetStorefrontThemesQuery} from "../../store/endpoints/StorefrontSettingsEndpoints";
import {calculateStyling, IStorefrontStyling} from "./StorefrontPreview";

interface IStorefrontSectionEditorProps {
    isGlobal?: boolean;
    storefront?: string;
    settings?: IStorefrontSettings;
    globalSettings?: IGlobalStorefrontSettings
}

const StorefrontSectionEditor: React.FC<IStorefrontSectionEditorProps> = (props) => {
    const [storefront, setStorefront] = useState<string>();
    useEffect(() => {
        setStorefront(props.isGlobal ? 'global' : props.storefront);
    },[props.isGlobal, props.storefront]);
    
    const [needsScroll, setNeedsScroll] = useState(false);
    const [saving, setSaving] = useState(false);
    const sections = useGetStorefrontSectionsQuery(storefront ?? '', {skip: !storefront});
    const [setSection] = useSaveStorefrontSectionMutation();
    const [setSections] = useSaveStorefrontSectionsMutation();
    const [deleteSection] = useDeleteStorefrontSectionMutation();
    const [effectiveSections, setEffectiveSections] = useState<IStorefrontSection[]>([]);
    const [canDrag, setCanDrag] = useState(true);
    const [showTemplatePanel, setShowTemplatePanel] = useState(false);
    const [styling, setStyling] = useState<IStorefrontStyling>();
    const [headerFont, setHeaderFont] = useState<string>();
    const [font, setFont] = useState<string>();
    const themes = useGetStorefrontThemesQuery();




    useEffect(() => {
        if(props.isGlobal) {
            setFont(props.globalSettings?.defaultFont)
            setHeaderFont(props.globalSettings?.headerFont)
            const styling = calculateStyling(
                props.globalSettings,
                undefined,
                undefined,
                props.globalSettings?.theme ? themes.data?.[props.globalSettings.theme] : undefined
            );
            setStyling(styling);
        }
        else{
            setFont(styleValue(props.settings, 'defaultFont') ?? props.globalSettings?.defaultFont)
            setHeaderFont(styleValue(props.settings, 'headerFont') ?? props.globalSettings?.headerFont)
            const styling = calculateStyling(
                props.globalSettings,
                props.settings,
                props.settings?.theme ? themes.data?.[props.settings.theme] : undefined,
                props.globalSettings?.theme ? themes.data?.[props.globalSettings.theme] : undefined
            );
            setStyling(styling);
        }

    }, [props.isGlobal, props.settings, props.globalSettings]);
    
    
    
    useEffect(() => {
        if (sections.data?.items) {
            setEffectiveSections(sections.data.items);
        }
    }, [sections.data?.items]);

    useEffect(() => {
        if (needsScroll) {
            scrollToBottom()
            setNeedsScroll(false);
        }
    }, [effectiveSections]);

    const [dragOver, setDragOver] = React.useState(false);
    const handleDragOverStart = () => setDragOver(true);
    const handleDragOverEnd = () => setDragOver(false);
    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        event.dataTransfer.setData('text', event.currentTarget.id);
    }
    const enableDropping = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }
    const handleDrop = (event: React.DragEvent<HTMLDivElement>, currentSection: number) => {
        const id = event.dataTransfer.getData('text')?.replace('section_', '');
        const sourceIndex = parseInt(id ?? '-1');
        if (sections.data?.items && sourceIndex !== -1 && currentSection !== -1) {
            const initialSections = [...sections.data.items];
            const [removed] = initialSections.splice(sourceIndex, 1); // Remove the dragged column
            initialSections.splice(currentSection, 0, removed); // Insert it at the current column's indexorder
            if(storefront && initialSections){
                const updatedSections = initialSections.map((section, index) => ({
                    ...section,
                    sortOrder: index + 1
                }));
                setSections({storefront: storefront ?? "", sections: updatedSections});
            }
        }
        setDragOver(false);
    }
    
    const updateSection = (index: number, section: IStorefrontSection, immediate?: boolean) => {
        const sections = cloneDeep(effectiveSections)

        sections[index] = section
        const updatedSections = sections.map((v, i) => ({ ...v, sortOrder: i + 1 }));
        setEffectiveSections([...updatedSections]);
        
        if(immediate && storefront){
            setSections({sections: updatedSections, storefront: storefront});
        }
        else {
            debouncedUpdateSections(sections)
        }
    }

    const debouncedUpdateSections = useCallback(
        debounce((val:IStorefrontSection[] | undefined) => {
            if(val && storefront) {
                setSections({sections: val, storefront: storefront});
            }
        }, 2000),
        [storefront]
    );

    const bottomRef = useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const commands: ICommandBarItemProps[] = [
        {
            split: true,
            key: 'add',
            text: 'Add Section',
            title: 'Add Section',
            disabled: saving,
            iconProps: {iconName: 'Add'},
            subMenuProps: {
                items: [
                    {
                        key: "fromTemplate",
                        text: "From Template",
                        iconProps: {iconName: 'WebTemplate'},
                        onClick: () => {setShowTemplatePanel(true)}
                    }
                ]
            },
            onClick: () => {
                
                const commandSections = [...(effectiveSections ?? [])];
                const headerCells = [
                    { type: 'image', title: "Image Cell", minWidth: 100, styleProps: { padding: '20px'} },
                    { type: 'text', title: "Text Cell", minWidth: 100, styleProps: { padding: '20px', font: font, color: styling?.textColor}},
                ]
                const newSection = {
                    title: 'New Section', 
                    linkUrl: '', 
                    type: 'featured', 
                    imageUrl: '',
                    maxEntries: 10, 
                    sortOrder: commandSections.length + 1, 
                    shared: props.isGlobal, 
                    disabled: true,
                    headerCells: headerCells,
                    titleStyleProps: { height: '50px', padding: '10px', borderTopLeftRadius: 10, borderTopRightRadius: 10, fontSize: '24px', font: headerFont, color: styling?.mbTextColor, backgroundColor: styling?.mbColor},
                    headerStyleProps: { padding: '10px', font: font, color: styling?.textColor},
                    styleProps:{ borderRadius: 10, borderWidth: '2px', borderStyle: 'solid', marginBottom:'20px', backgroundColor: styling?.bgColor},
                    footerStyleProps: { padding: '10px', font: font, color: styling?.textColor},
                } as IStorefrontSection;
                if(storefront) {
                    setSection({section: newSection, storefront: storefront}).unwrap().then(() => {
                        setNeedsScroll(true);
                    })
                }
            }
        }
    ]
    
    const onMoveUp = (index: number) => {
        if (effectiveSections && storefront) {
            const sections = [...effectiveSections];
            const [movedSection] = sections.splice(index, 1);
            sections.splice(index - 1, 0, movedSection);
            setSaving(true);
            const updatedSections = sections.map((v, i) => ({ ...v, sortOrder: i + 1 }));
            setSections({sections: updatedSections, storefront: storefront}).unwrap().then(() => {setSaving(false)});
        }
    }
    
    const onMoveDown = (index: number) => {
        if (effectiveSections && storefront) {
            const sections = [...effectiveSections];
            const [movedSection] = sections.splice(index, 1);
            sections.splice(index + 1, 0, movedSection);
            setSaving(true);
            const updatedSections = sections.map((v, i) => ({ ...v, sortOrder: i + 1 }));
            setSections({sections: updatedSections, storefront: storefront}).unwrap().then(() => {setSaving(false)});
        }
    }
    
    const onDelete = (index: number) => {
        const sectionToDelete = effectiveSections?.[index];
        if (sectionToDelete && sectionToDelete.id && storefront) {
            setSaving(true);
            deleteSection({storefront: storefront, id: sectionToDelete.id}).unwrap().then(() => {
                setSaving(false);
            })
        }
    }

    return(
            <div style={{width: '90%', minWidth: '300px'}}>
                {(saving || sections.isLoading || sections.isFetching) ?
                    <div style={{alignSelf: 'self-start'}}><Spinner label="Loading..." size={SpinnerSize.small} ariaLive="assertive"/></div> : <CommandBar items={commands} farItems={[]}/>
                }
                {(storefront && showTemplatePanel) && <StorefrontSectionTemplateSelectorModal isGlobal={props.isGlobal} storefront={storefront} displayModal={showTemplatePanel} closer={() => setShowTemplatePanel(false)}/>}
                {(effectiveSections ?? []).map((section, idx) => (
                    <div id={`section-${idx}`} key={idx} draggable={canDrag}
                         onDragOver={enableDropping}
                         onDrop={(ev) => handleDrop(ev, idx)}
                         onDragEnter={handleDragOverStart}
                         onDragLeave={handleDragOverEnd}
                         style={dragOver ? {width: '100%', height: '100%', background: 'rgb(207, 220, 181)'} : {}}
                     >
                        <div id={`section_${idx}`} className={'storefront-section'} draggable={canDrag} onDragStart={handleDragStart}>
                            <StorefrontSection
                                onEdit={() => setCanDrag(false)}
                                onEditComplete={() => setCanDrag(true)}
                                saving={saving}
                                currentIndex={idx}
                                onMoveUp={onMoveUp}
                                onMoveDown={onMoveDown}
                                section={section}
                                isGlobal={props.isGlobal}
                                storefront={storefront}
                                canMoveDown={(idx < (effectiveSections.length - 1))}
                                onChange={(s, now) => updateSection(idx, s, now)}
                                onDelete={onDelete}
                            />
                        </div>
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>
    )
}
export default StorefrontSectionEditor;

